import { useMemo } from 'react'
import { useQuery } from '@tanstack/react-query'
import { api } from '@api'
import { useCurrentUserStore } from '@store'
import { PaginatedResponse } from './types'
import { ContactRequest } from './usePaginateContactRequests'

const PAGE_SIZE = 100
// Safety stop: 1 000 requests waiting for an answer is far beyond real use.
const MAX_PAGES = 10

const fetchPendingContactRequests = async (): Promise<ContactRequest[]> => {
  const requests: ContactRequest[] = []
  for (let page = 1; page <= MAX_PAGES; page++) {
    const { data } = await api.get<PaginatedResponse<ContactRequest>>(
      `/contact-requests?status=PENDING&limit=${PAGE_SIZE}&page=${page}`,
    )
    requests.push(...data.data)
    if (data.data.length === 0 || requests.length >= data.metadata.count) break
  }
  return requests
}

// Every contact request still waiting for an answer, sent or received. The
// server gives 10 requests per page, all statuses mixed, in no useful order:
// reading the first page only made « Ajouter » come back for a request that
// was still waiting.
export const usePendingContactRequests = () => {
  const { userID } = useCurrentUserStore()
  const query = useQuery<ContactRequest[]>({
    // Under 'contact-requests': answering or sending a request refreshes it.
    queryKey: ['contact-requests', 'pending'],
    queryFn: fetchPendingContactRequests,
  })

  const split = useMemo(() => {
    const sent: ContactRequest[] = []
    const received: ContactRequest[] = []
    const sentByRecipientId = new Map<string, string>()
    const receivedBySenderId = new Map<string, string>()
    query.data?.forEach((request) => {
      if (request.sender.id === userID) {
        sent.push(request)
        sentByRecipientId.set(request.recipient.id, request.id)
      } else {
        received.push(request)
        receivedBySenderId.set(request.sender.id, request.id)
      }
    })
    return { sent, received, sentByRecipientId, receivedBySenderId }
  }, [query.data, userID])

  return { ...query, ...split }
}
